import type { SearchHit } from '@rfleet/shared';
import { useQuery } from '@tanstack/react-query';
import { MapPin, Route, Search, Truck, UserRound, Users } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';

const ICON = { vehicle: Truck, driver: UserRound, trip: Route, customer: Users, place: MapPin } as const;

/** Header search: debounced unified search with keyboard navigation and ⌘K / Ctrl+K focus. */
export function SearchBox() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(id);
  }, [q]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        input.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const res = useQuery({ queryKey: ['search', debounced], queryFn: () => api<SearchHit[]>(`/search?q=${encodeURIComponent(debounced)}`), enabled: debounced.length >= 2 });
  const hits = res.data ?? [];
  const go = (h: SearchHit) => { setOpen(false); setQ(''); nav(h.href); };

  return (
    <div className="relative hidden w-80 md:block">
      <label className="flex items-center gap-2.5 rounded-md border border-border bg-surface-2 px-3.5 py-2 text-[13px] text-text-muted focus-within:border-brand-ink">
        <Search size={16} aria-hidden />
        <input
          ref={input}
          className="w-full bg-transparent outline-none placeholder:text-text-muted"
          placeholder={t('header.search')}
          aria-label={t('common.search')}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); setActive(0); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, hits.length - 1)); }
            if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
            if (e.key === 'Enter' && hits[active]) go(hits[active]);
            if (e.key === 'Escape') setOpen(false);
          }}
        />
        <kbd className="hidden rounded border border-border px-1.5 text-[10px] text-text-faint lg:inline">Ctrl K</kbd>
      </label>
      {open && debounced.length >= 2 ? (
        <ul className="absolute inset-x-0 top-full z-40 mt-1 max-h-80 overflow-y-auto rounded-lg border border-border bg-surface py-1 shadow-overlay" role="listbox">
          {res.isLoading ? <li className="px-3 py-2 text-xs text-text-muted">{t('common.loading')}</li> : !hits.length ? <li className="px-3 py-2 text-xs text-text-muted">{t('common.empty')}</li> : hits.map((h, i) => {
            const Icon = ICON[h.kind];
            return (
              <li key={`${h.kind}:${h.id}`} role="option" aria-selected={i === active}>
                <button className={`flex w-full items-center gap-3 px-3 py-2 text-start text-sm ${i === active ? 'bg-brand-soft' : 'hover:bg-surface-hover'}`} onMouseDown={(e) => e.preventDefault()} onClick={() => go(h)}>
                  <Icon size={16} className="text-text-muted" aria-hidden />
                  <span className="min-w-0 flex-1"><span className="block truncate font-medium">{h.title}</span>{h.subtitle ? <span className="block truncate text-xs text-text-muted">{h.subtitle}</span> : null}</span>
                  <span className="text-[10px] uppercase text-text-faint">{t(`search.${h.kind}`)}</span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
